import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, map, Observable, take } from 'rxjs';
import { ApplePayComponent } from '../../components/apple-pay/apple-pay.component';
import { GooglePayComponent } from '../../components/google-pay/google-pay.component';
import { AutoFocusTransitionDirective } from '../../directives/auto-focus-transition.directive';
import { NameSanitizerDirective } from '../../directives/name-sanitizer.directive';
import { PanFormatterDirective } from '../../directives/pan-formatter.directive';
import { TPSError } from '../../services/api/api.models';
import { ECommError, ErrorService } from '../../services/error/error.service';
import { ExpressService } from '../../services/express/express.service';
import { ExternalDataService } from '../../services/external-data/external-data.service';
import { PanTokenizationService } from '../../services/pan-tokenization/pan-tokenization.service';
import { EXPIRATION_DATE_INVALID, expirationDateValidator } from '../../validators/expiration-date-validator';
import { luhnValidator } from '../../validators/luhn-validators';
import { maxLengthWithoutSpacesValidator } from '../../validators/max-length-without-spaces-validator';
import { minLengthWithoutSpacesValidator } from '../../validators/min-length-without-spaces-validator';
import { monthFormatValidator } from '../../validators/month-format-validator';

type FormValues = Partial<{
  pan: string | null;
  expDateYear: string | null;
  expDateMonth: string | null;
  cardOwnerName: string | null;
}>;

@Component({
  selector: 'app-pan-tokenization',
  imports: [ReactiveFormsModule, CommonModule, TranslateModule, AutoFocusTransitionDirective, NameSanitizerDirective, PanFormatterDirective, ApplePayComponent, GooglePayComponent],
  templateUrl: './pan-tokenization.component.html',
  styleUrl: './pan-tokenization.component.scss'
})
export class PanTokenizationComponent implements OnInit {
  public formGroup = new FormGroup(
    {
      pan: new FormControl('', [Validators.required, luhnValidator(), minLengthWithoutSpacesValidator(13), maxLengthWithoutSpacesValidator(19)]),
      expDateMonth: new FormControl('', [Validators.required, monthFormatValidator()]),
      expDateYear: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{2}$/)]),
      cardOwnerName: new FormControl('', { validators: Validators.required })
    },
    {
      updateOn: 'change'
    }
  );

  public fc = this.formGroup.controls;

  public panCanBeInError = false;
  public expirationCanBeInError = false;
  public nameCanBeInError = false;

  // Show errors
  get showPanError() {
    return this.fc.pan.invalid && this.panCanBeInError;
  }

  get showExpirationError() {
    return (this.fc.expDateMonth.invalid || this.fc.expDateYear.invalid) && this.expirationCanBeInError;
  }

  get showNameError() {
    return this.fc.cardOwnerName.invalid && this.nameCanBeInError;
  }

  // Specific errors
  get expirationDateInPast() {
    return (
      (this.fc.expDateYear.dirty || this.fc.expDateYear.touched) &&
      (this.fc.expDateMonth.dirty || this.fc.expDateMonth.touched) &&
      this.formGroup.hasError(EXPIRATION_DATE_INVALID)
    );
  }

  public $isProcessedOrProcessing: Observable<boolean>;
  public $isDoing3DAuth: Observable<boolean>;
  public $panFormatting: Observable<boolean>;
  public $showExtendedFields: Observable<boolean>;
  public $showNameField: Observable<boolean>;
  public $showExpressCheckout: Observable<boolean>;
  public $placeholders: Observable<boolean>;
  public $permissiveSubmitButton: Observable<boolean>;
  public $panTokenButtonKey: Observable<string>;
  public $tpsErrors: Observable<TPSError[]>;

  public $ecommErrors: Observable<ECommError[]>;

  public $isExpressEnabled: Observable<boolean>;
  public $displayApplePay: Observable<boolean>;
  public $displayGooglePay: Observable<boolean>;

  constructor(
    private panTokenizationService: PanTokenizationService,
    private externalDataService: ExternalDataService,
    private expressService: ExpressService,
    private errorService: ErrorService
  ) {
    this.$isProcessedOrProcessing = this.panTokenizationService.$isProcessingOrProcessed;
    this.$isDoing3DAuth = this.panTokenizationService.$isDoing3DAuth;
    this.$panFormatting = this.externalDataService.$panFormatting;
    this.$showExtendedFields = this.externalDataService.$useShortCardTokenForm.pipe(map(useShort => !useShort));
    this.$showNameField = combineLatest([
      this.$showExtendedFields,
      this.externalDataService.$use3DSCardTokenForm
    ]).pipe(map(([showExtended, use3DS]) => showExtended && use3DS));
    this.$showExpressCheckout = this.externalDataService.$useExpressCheckout;
    this.$placeholders = this.externalDataService.$placeholders;
    this.$permissiveSubmitButton = this.externalDataService.$permissiveSubmitButton;
    this.$panTokenButtonKey = this.externalDataService.$stylesheetName.pipe(
      map(name => ['legacy_tallinn', 'legacy_parnu', 'legacy_tartu', 'legacy_bo', 'legacy_emv', 'legacy_elron'].includes(name)
        ? 'panTokenGeneration.search' : 'panTokenGeneration.generate')
    );
    this.$tpsErrors = this.errorService.$tpsErrors;
    this.$ecommErrors = this.errorService.$ecomErrors;

    this.$isExpressEnabled = this.expressService.$isExpressEnabled;
    this.$displayApplePay = this.expressService.$canDoApplePay;
    this.$displayGooglePay = this.expressService.$canDoGooglePay;

    this.fc.pan.valueChanges.subscribe(() => this.panCanBeInError = false);
    this.fc.expDateMonth.valueChanges.subscribe(() => this.expirationCanBeInError = false);
    this.fc.expDateYear.valueChanges.subscribe(() => this.expirationCanBeInError = false);
    this.fc.cardOwnerName.valueChanges.subscribe(() => this.nameCanBeInError = false);
  }

  // unsubscribe on destroy?
  ngOnInit(): void {
    combineLatest([
      this.panTokenizationService.$isProcessingOrProcessed,
      this.externalDataService.$useShortCardTokenForm,
      this.externalDataService.$use3DSCardTokenForm
    ]).subscribe(([isProcessingOrProcessed, useShortCardTokenForm, use3DSCardTokenForm]) => {
      if (!isProcessingOrProcessed) {
        this.fc.pan.enable();
      } else {
        this.fc.pan.disable();
      }

      if (!isProcessingOrProcessed && !useShortCardTokenForm) {
        this.fc.expDateMonth.enable();
        this.fc.expDateYear.enable();
        this.formGroup.setValidators(expirationDateValidator('expDateMonth', 'expDateYear'));
      } else {
        this.fc.expDateMonth.disable();
        this.fc.expDateYear.disable();
        this.formGroup.clearValidators();
      }

      if (!isProcessingOrProcessed && !useShortCardTokenForm && use3DSCardTokenForm) {
        this.fc.cardOwnerName.enable();
      } else {
        this.fc.cardOwnerName.disable();
      }

      this.formGroup.updateValueAndValidity();
    });
  }

  onSubmit() {
    this.panCanBeInError = true;
    this.expirationCanBeInError = true;
    this.nameCanBeInError = true;

    if (this.formGroup.invalid) {
      return;
    }

    this.panTokenizationService.$isProcessingOrProcessed.next(true);
    this.errorService.clearTpsErrors();

    combineLatest([
      this.externalDataService.$useShortCardTokenForm,
      this.externalDataService.$use3DSCardTokenForm
    ]).pipe(take(1)).subscribe(([useShortCardTokenForm, use3DSCardTokenForm]) => {
      const formValue = { ...this.formGroup.value, pan: this.formGroup.value.pan?.replaceAll(' ', '') };
      if (!useShortCardTokenForm && use3DSCardTokenForm) {
        this.submitPanTokenFormWith3DS(formValue);
      } else {
        this.submitPanTokenForm(formValue);
      }
    });
  }

  private submitPanTokenForm(formValues: FormValues) {
    if (formValues.pan && formValues.expDateYear && formValues.expDateMonth) {
      const expiryDate = this.panTokenizationService.compileExpiryDate(formValues.expDateYear, formValues.expDateMonth);
      this.panTokenizationService.sendPanTokenGenerationRequestWithCardNumber(formValues.pan, expiryDate);
    } else if (formValues.pan) {
      this.panTokenizationService.sendPanTokenGenerationRequestWithCardNumber(formValues.pan, null);
    }
  }

  private submitPanTokenFormWith3DS(formValues: FormValues) {
    if (formValues.pan && formValues.expDateYear && formValues.expDateMonth && formValues.cardOwnerName) {
      this.panTokenizationService.sendPanTokenGenerationWith3DSecure(
        formValues.pan,
        formValues.expDateYear,
        formValues.expDateMonth,
        formValues.cardOwnerName
      );
    }
  }


}
